/**
 * Restores explicitly marked, entry-scoped UI state without knowing any
 * feature-specific surface. Long-lived form values remain untouched unless
 * their individual control opts in with data-reset-on-enter.
 */
export function resetTransientSurfaceState(scope: HTMLElement): void {
	forEachMarked(scope, '[data-transient-feedback]', (element) => {
		element.textContent = '';
		element.hidden = true;
		element.removeAttribute('data-feedback-kind');
	});

	forEachMarked(scope, '[data-transient-action]', (element) => {
		element.removeAttribute('data-submitting');
		element.removeAttribute('data-ui-attempt');
		element.removeAttribute('data-locked');
		element.removeAttribute('data-loading');
		element.removeAttribute('aria-busy');
		const idleLabel = element.getAttribute('data-idle-label');
		if (idleLabel !== null) setTransientActionLabel(element, idleLabel);
		const idleDisabled = element.getAttribute('data-idle-disabled');
		if (idleDisabled === 'owner') return;
		if ((idleDisabled === 'true' || idleDisabled === 'false') && 'disabled' in element) {
			(element as HTMLButtonElement).disabled = idleDisabled === 'true';
			element.setAttribute('aria-disabled', idleDisabled);
		}
	});

	forEachMarked(scope, '[data-reset-on-enter]', (element) => {
		resetControl(element);
		element.classList.remove('input-error');
		element.removeAttribute('aria-invalid');
		const transientErrorId = element.getAttribute('aria-errormessage');
		const transientError = transientErrorId ? element.ownerDocument?.getElementById(transientErrorId) : null;
		if (transientError && scope.contains(transientError) && transientError.classList.contains('wizard-field-error')) transientError.remove();
		if (transientErrorId && element.getAttribute('aria-describedby') === transientErrorId) element.removeAttribute('aria-describedby');
		element.removeAttribute('aria-errormessage');
	});

	forEachMarked(scope, '[data-reset-view]', (element) => {
		switch (element.dataset.resetView) {
			case 'show':
				element.hidden = false;
				element.style.display = '';
				break;
			case 'hide':
				element.hidden = true;
				element.style.display = 'none';
				break;
			case 'scroll-start':
				element.scrollLeft = 0;
				element.scrollTop = 0;
				break;
		}
	});
}

export function setTransientActionLabel(element: HTMLElement, label: string): void {
	const target = element.querySelector<HTMLElement>('[data-idle-label-target]');
	if (target) target.textContent = label;
	else element.textContent = label;
}

function forEachMarked(scope: HTMLElement, selector: string, callback: (element: HTMLElement) => void): void {
	const elements = Array.from(scope.querySelectorAll<HTMLElement>(selector));
	if (scope.matches(selector)) elements.unshift(scope);
	elements.forEach(callback);
}

function resetControl(element: HTMLElement): void {
	if (element instanceof HTMLInputElement) {
		if (element.type === 'checkbox' || element.type === 'radio') element.checked = element.defaultChecked;
		else element.value = element.defaultValue;
		return;
	}
	if (element instanceof HTMLTextAreaElement) {
		element.value = element.defaultValue;
		return;
	}
	if (element instanceof HTMLSelectElement) {
		const defaultIndex = Array.from(element.options).findIndex((option) => option.defaultSelected);
		element.selectedIndex = defaultIndex >= 0 ? defaultIndex : 0;
	}
}
